import type { Action } from 'redux';

import normaliseError, { type NormaliseErrorInput } from '/shared/functions/normaliseError';
import { dashboardTabs } from '/business/shared/util/Statics';
import type { AuthUser } from '/business/shared/state/reducers/auth';
import type { NormalisedError } from '/index';

import type {
  GetApiB2bApplicationByIdGetResponse,
  GetApiB2bPackagesResponse,
  GetApiB2bAnnualUpdatesResponse,
  GetApiB2bPackageByIdComparisonResponse,
  GetApiB2bApplicationByIdCopiedFromSnapshotsResponse,
  GetApiB2bApplicationByIdFormStateResponses,
  PostApiB2bApplicationByIdMarkAnnualUpdateCompleteResponse,
} from '/api';

// Extract the single item type from the response
export type Application = GetApiB2bApplicationByIdGetResponse;
export type ApplicationPackage = GetApiB2bPackagesResponse[number];
type Package = ApplicationPackage;
type AnnualUpdateApplication = GetApiB2bAnnualUpdatesResponse[number];
type ApplicationInvite = Application['invites'][number];
type FormStateResponse = GetApiB2bApplicationByIdFormStateResponses[200];
type FullFormStateResponse = Extract<FormStateResponse, { state: { items: object[] } }>;

interface ApplicationEmissions {
  carbonIntensity?: number;
  carbonTonnes?: number;
  carbonTonnesWithSafetyFactor?: number;
  safetyFactor?: number;
  safetyTonnes?: number;
  scope2EmissionsCarbonIntensity?: number;
}

// Define the extended application type with extra fields added by the reducer beyond the API response
export interface ExtendedApplication extends Application {
  emissions?: ApplicationEmissions;
  averages?: FullFormStateResponse['averages'];
  basketItems?: FullFormStateResponse['state']['items'];
  basketMeta?: FullFormStateResponse['state']['meta'];
  questionAnswerStructured?: FullFormStateResponse['questionAnswerStructured'];
  detailsEmail?: string;
}

interface ApplicationFormStatePayload {
  averages?: ExtendedApplication['averages'];
  applicationState?: Application['applicationState'];
  basketItems?: ExtendedApplication['basketItems'];
  basketMeta?: ExtendedApplication['basketMeta'];
  emissions?: ApplicationEmissions;
  offset?: Application['offset'];
  paymentAwaitingProcessing?: boolean;
  questionAnswerStructured?: ExtendedApplication['questionAnswerStructured'];
}

interface ApplicationRequestError {
  applicationId: string;
  error: NormalisedError;
}

export interface ApplicationsState {
  items: ExtendedApplication[] | null;
  packages: ApplicationPackage[] | null;
  annualUpdates: AnnualUpdateApplication[] | null;
  fetchingAnnualUpdates: boolean;
  fetchedAnnualUpdates: boolean;
  fetchingPackages: boolean;
  fetchedPackages: boolean;
  fetchingCopiedFromSnapshots: boolean;
  fetchingComparison: boolean;
  comparison: GetApiB2bPackageByIdComparisonResponse | null;
  copiedFromSnapshots: GetApiB2bApplicationByIdCopiedFromSnapshotsResponse | null;
  saving: boolean;
  saved: boolean;
  fetching: boolean;
  fetched: boolean;
  syncing: boolean;
  synced: boolean;
  error: NormalisedError | null;
  token: string | null;
  dashboardApplicationsState: string;
  activeApplicationId: string | null;
  hasInitiallySyncedApplications: boolean;
  newlyCreatedApplicationOwnerEmail: string | null;
  syncingFormState?: boolean;
  applicationLoadError: ApplicationRequestError | null;
  formStateLoadError: ApplicationRequestError | null;
}

const defaultState: ApplicationsState = {
  items: null,
  packages: null,
  annualUpdates: null,
  fetchingAnnualUpdates: false,
  fetchedAnnualUpdates: false,
  fetchingPackages: false,
  fetchedPackages: false,
  fetchingCopiedFromSnapshots: false,
  fetchingComparison: false,
  comparison: null,
  copiedFromSnapshots: null,
  saving: false,
  saved: false,
  fetching: false,
  fetched: false,
  syncing: false,
  synced: false,
  error: null,
  token: null,
  dashboardApplicationsState: dashboardTabs.VIEW_ALL,
  activeApplicationId: null,
  hasInitiallySyncedApplications: false,
  newlyCreatedApplicationOwnerEmail: null,
  applicationLoadError: null,
  formStateLoadError: null,
};

// Define action interfaces
interface ClearApplicationsAction extends Action {
  type: 'CLEAR_APPLICATIONS';
}

interface CreateApplicationStartedAction extends Action {
  type: 'CREATE_APPLICATION_STARTED';
  newlyCreatedApplicationOwnerEmail: string;
}

interface CreateApplicationFulfilledAction extends Action {
  type: 'CREATE_APPLICATION_FULFILLED';
  application: ExtendedApplication;
}

interface CreateApplicationRejectedAction extends Action {
  type: 'CREATE_APPLICATION_REJECTED';
  error: NormaliseErrorInput;
}

interface ClearAuthenticationAction extends Action {
  type: 'CLEAR_AUTHENTICATION';
}

interface SetApplicationActiveStartedAction extends Action {
  type: 'SET_APPLICATION_ACTIVE_STARTED';
}

interface SetApplicationActiveFulfilledAction extends Action {
  type: 'SET_APPLICATION_ACTIVE_FULFILLED';
  applicationId: string;
}

interface SetApplicationActiveRejectedAction extends Action {
  type: 'SET_APPLICATION_ACTIVE_REJECTED';
}

interface SetIsSyncingApplicationsAction extends Action {
  type: 'SET_IS_SYNCING_APPLICATIONS';
  status: boolean;
}

interface SetDashboardApplicationsStateAction extends Action {
  type: 'SET_DASHBOARD_APPLICATIONS_STATE';
  applicationState: string;
}

interface EditApplicationStartedAction extends Action {
  type: 'EDIT_APPLICATION_STARTED';
}

interface SaveFormStateStartedAction extends Action {
  type: 'SAVE_FORM_STATE_STARTED';
}

interface SaveFormStateRejectedAction extends Action {
  type: 'SAVE_FORM_STATE_REJECTED';
  error: NormaliseErrorInput;
}

interface GetFormStateStartedAction extends Action {
  type: 'GET_FORM_STATE_STARTED';
  applicationId: number | string;
}

interface GetFormStateResetAction extends Action {
  type: 'GET_FORM_STATE_RESET';
}

interface GetFormStateRejectedAction extends Action {
  type: 'GET_FORM_STATE_REJECTED';
  applicationId: number | string;
  error: NormaliseErrorInput;
}

interface GetFormStatePartialFulfilledAction extends Action, Pick<ApplicationFormStatePayload, 'emissions'> {
  type: 'GET_FORM_STATE_PARTIAL_FULFILLED';
  id: number;
}

interface GetFormStateFulfilledAction extends Action, ApplicationFormStatePayload {
  type: 'GET_FORM_STATE_FULFILLED';
  id: number;
  applicationState: Application['applicationState'];
  offset: Application['offset'];
  paymentAwaitingProcessing: boolean;
}

interface MarkApplicationPaymentPendingStateAction extends Action {
  type: 'MARK_APPLICATION_PAYMENT_PENDING_STATE';
  applicationId: number;
  isDone: boolean;
}

interface EditApplicationRejectedAction extends Action {
  type: 'EDIT_APPLICATION_REJECTED';
  error: NormaliseErrorInput;
}

interface TransitionApplicationStartedAction extends Action {
  type: 'TRANSITION_APPLICATION_STARTED';
}

interface ApplicationLoadStartedAction extends Action {
  type: 'APPLICATION_LOAD_START';
  applicationId: number | string;
}

interface ApplicationLoadFulfilledAction extends Action {
  type: 'APPLICATION_LOAD_FULFILLED';
  application: ExtendedApplication;
  applicationId: number | string;
}

interface ApplicationLoadRejectedAction extends Action {
  type: 'APPLICATION_LOAD_REJECTED';
  applicationId: number | string;
  error?: NormaliseErrorInput;
}

interface TransitionApplicationRejectedAction extends Action {
  type: 'TRANSITION_APPLICATION_REJECTED';
  error: NormaliseErrorInput;
}

interface SaveInviteStatusFulfilledAction extends Action {
  type: 'SAVE_INVITE_STATUS_FULFILLED';
  application: ExtendedApplication;
}

interface SendInviteStatusFulfilledAction extends Action {
  type: 'SEND_INVITE_STATUS_FULFILLED';
  invite: ApplicationInvite;
  id: number;
}

interface RemoveInviteStatusFulfilledAction extends Action {
  type: 'REMOVE_INVITE_STATUS_FULFILLED';
  removedId: number;
  applicationId: number;
}

interface GetApplicationPackagesStartedAction extends Action {
  type: 'GET_APPLICATION_PACKAGES_STARTED';
}

interface GetApplicationPackagesFulfilledAction extends Action {
  type: 'GET_APPLICATION_PACKAGES_FULFILLED';
  applicationPackages: Package[];
}

interface GetApplicationPackagesRejectedAction extends Action {
  type: 'GET_APPLICATION_PACKAGES_REJECTED';
  error: NormaliseErrorInput;
}

interface GetAnnualUpdatesStartedAction extends Action {
  type: 'GET_ANNUAL_UPDATES_STARTED';
}

interface GetAnnualUpdatesFulfilledAction extends Action {
  type: 'GET_ANNUAL_UPDATES_FULFILLED';
  annualUpdates: AnnualUpdateApplication[];
}

interface GetAnnualUpdatesRejectedAction extends Action {
  type: 'GET_ANNUAL_UPDATES_REJECTED';
  error: NormaliseErrorInput;
}

interface EnableAnnualUpdateStartedAction extends Action {
  type: 'ENABLE_ANNUAL_UPDATE_STARTED';
  applicationId: number | string;
  packageId: number | string;
}

interface EnableAnnualUpdateFulfilledAction extends Action {
  type: 'ENABLE_ANNUAL_UPDATE_FULFILLED';
  applicationId: number | string;
  packageId: number | string;
  newApplication: Application;
}

interface EnableAnnualUpdateRejectedAction extends Action {
  type: 'ENABLE_ANNUAL_UPDATE_REJECTED';
  error: NormaliseErrorInput;
}

interface ArchivePackageStartedAction extends Action {
  type: 'ARCHIVE_PACKAGE_STARTED';
  packageId: number | string;
}

interface ArchivePackageFulfilledAction extends Action {
  type: 'ARCHIVE_PACKAGE_FULFILLED';
  packageId: number | string;
}

interface ArchivePackageRejectedAction extends Action {
  type: 'ARCHIVE_PACKAGE_REJECTED';
  error: NormaliseErrorInput;
}

interface MarkAnnualUpdateCompleteStartedAction extends Action {
  type: 'MARK_ANNUAL_UPDATE_COMPLETE_STARTED';
  applicationId: number;
}

interface MarkAnnualUpdateCompleteFulfilledAction extends Action {
  type: 'MARK_ANNUAL_UPDATE_COMPLETE_FULFILLED';
  applicationId: number;
  data: PostApiB2bApplicationByIdMarkAnnualUpdateCompleteResponse;
}

interface MarkAnnualUpdateCompleteRejectedAction extends Action {
  type: 'MARK_ANNUAL_UPDATE_COMPLETE_REJECTED';
  error: NormaliseErrorInput;
}

interface GetApplicationPackageComparisonStartedAction extends Action {
  type: 'GET_APPLICATION_PACKAGE_COMPARISON_STARTED';
  packageId: number | string;
}

interface GetApplicationPackageComparisonFulfilledAction extends Action {
  type: 'GET_APPLICATION_PACKAGE_COMPARISON_FULFILLED';
  packageId: number | string;
  payload: GetApiB2bPackageByIdComparisonResponse;
}

interface GetApplicationPackageComparisonRejectedAction extends Action {
  type: 'GET_APPLICATION_PACKAGE_COMPARISON_REJECTED';
  error: NormaliseErrorInput;
}

interface GetCopiedFromSnapshotsStartedAction extends Action {
  type: 'GET_COPIED_FROM_SNAPSHOTS_STARTED';
  applicationId: number | string;
}

interface GetCopiedFromSnapshotsFulfilledAction extends Action {
  type: 'GET_COPIED_FROM_SNAPSHOTS_FULFILLED';
  applicationId: number | string;
  payload: GetApiB2bApplicationByIdCopiedFromSnapshotsResponse;
}

interface GetCopiedFromSnapshotsRejectedAction extends Action {
  type: 'GET_COPIED_FROM_SNAPSHOTS_REJECTED';
  error: NormaliseErrorInput;
}

interface SetApplicationPackageBaselineYearStartedAction extends Action {
  type: 'SET_APPLICATION_PACKAGE_BASELINE_YEAR_STARTED';
  packageId: number | string;
  baselineApplicationId: number | null;
}

interface SetApplicationPackageBaselineYearFulfilledAction extends Action {
  type: 'SET_APPLICATION_PACKAGE_BASELINE_YEAR_FULFILLED';
  packageId: number | string;
  baselineApplicationId: number | null;
  payload: GetApiB2bPackageByIdComparisonResponse;
}

interface SetApplicationPackageBaselineYearRejectedAction extends Action {
  type: 'SET_APPLICATION_PACKAGE_BASELINE_YEAR_REJECTED';
  error: NormaliseErrorInput;
}

interface FetchAuthFulfilledAction extends Action {
  type: 'FETCH_AUTH_FULFILLED';
  user: AuthUser;
  silent?: boolean;
}

// Union type for all actions
type ApplicationsAction =
  | ClearApplicationsAction
  | CreateApplicationStartedAction
  | CreateApplicationFulfilledAction
  | CreateApplicationRejectedAction
  | ClearAuthenticationAction
  | SetApplicationActiveStartedAction
  | SetApplicationActiveFulfilledAction
  | SetApplicationActiveRejectedAction
  | SetIsSyncingApplicationsAction
  | SetDashboardApplicationsStateAction
  | EditApplicationStartedAction
  | SaveFormStateStartedAction
  | SaveFormStateRejectedAction
  | GetFormStateStartedAction
  | GetFormStateResetAction
  | GetFormStateRejectedAction
  | GetFormStatePartialFulfilledAction
  | GetFormStateFulfilledAction
  | MarkApplicationPaymentPendingStateAction
  | EditApplicationRejectedAction
  | TransitionApplicationStartedAction
  | ApplicationLoadStartedAction
  | ApplicationLoadFulfilledAction
  | ApplicationLoadRejectedAction
  | TransitionApplicationRejectedAction
  | SaveInviteStatusFulfilledAction
  | SendInviteStatusFulfilledAction
  | RemoveInviteStatusFulfilledAction
  | GetApplicationPackagesStartedAction
  | GetApplicationPackagesFulfilledAction
  | GetApplicationPackagesRejectedAction
  | GetAnnualUpdatesStartedAction
  | GetAnnualUpdatesFulfilledAction
  | GetAnnualUpdatesRejectedAction
  | EnableAnnualUpdateStartedAction
  | EnableAnnualUpdateFulfilledAction
  | EnableAnnualUpdateRejectedAction
  | ArchivePackageStartedAction
  | ArchivePackageFulfilledAction
  | ArchivePackageRejectedAction
  | MarkAnnualUpdateCompleteStartedAction
  | MarkAnnualUpdateCompleteFulfilledAction
  | MarkAnnualUpdateCompleteRejectedAction
  | FetchAuthFulfilledAction
  | GetApplicationPackageComparisonStartedAction
  | GetApplicationPackageComparisonFulfilledAction
  | GetApplicationPackageComparisonRejectedAction
  | SetApplicationPackageBaselineYearStartedAction
  | SetApplicationPackageBaselineYearFulfilledAction
  | SetApplicationPackageBaselineYearRejectedAction
  | GetCopiedFromSnapshotsStartedAction
  | GetCopiedFromSnapshotsFulfilledAction
  | GetCopiedFromSnapshotsRejectedAction;

function reducer(state: ApplicationsState = defaultState, action: ApplicationsAction): ApplicationsState {
  switch (action.type) {
    case 'CLEAR_APPLICATIONS': {
      return defaultState;
    }
    case 'CREATE_APPLICATION_STARTED': {
      const { newlyCreatedApplicationOwnerEmail } = action;
      return {
        ...state,
        saving: true,
        saved: false,
        error: null,
        newlyCreatedApplicationOwnerEmail,
      };
    }
    case 'CREATE_APPLICATION_FULFILLED': {
      return {
        ...state,
        saving: false,
        saved: true,
        error: null,
        items: state.items ? [...state.items, action.application] : [action.application],
        hasInitiallySyncedApplications: false,
      };
    }
    case 'CREATE_APPLICATION_REJECTED': {
      return {
        ...state,
        saving: false,
        saved: false,
        error: normaliseError(action.error),
        newlyCreatedApplicationOwnerEmail: null,
      };
    }
    case 'CLEAR_AUTHENTICATION': {
      return {
        ...state,
        ...defaultState,
      };
    }
    case 'FETCH_AUTH_FULFILLED': {
      if (action.silent) {
        return state;
      }
      return {
        ...state,
        hasInitiallySyncedApplications: false,
        fetchedAnnualUpdates: false,
        fetchingPackages: false,
        fetchedPackages: false,
      };
    }
    case 'SET_APPLICATION_ACTIVE_FULFILLED': {
      return {
        ...state,
        activeApplicationId: action.applicationId,
        fetching: false,
      };
    }
    case 'SET_APPLICATION_ACTIVE_REJECTED': {
      return {
        ...state,
        activeApplicationId: null,
        fetching: false,
      };
    }
    case 'SET_IS_SYNCING_APPLICATIONS': {
      return {
        ...state,
        hasInitiallySyncedApplications: action.status,
      };
    }
    case 'SET_DASHBOARD_APPLICATIONS_STATE': {
      return {
        ...state,
        dashboardApplicationsState: action.applicationState,
      };
    }
    case 'EDIT_APPLICATION_STARTED': {
      return {
        ...state,
        saving: true,
        saved: false,
        error: null,
      };
    }
    case 'SAVE_FORM_STATE_STARTED': {
      return {
        ...state,
        syncing: true,
        synced: false,
      };
    }
    case 'SAVE_FORM_STATE_REJECTED': {
      return {
        ...state,
        syncing: false,
        synced: false,
        error: normaliseError(action.error),
      };
    }
    case 'GET_FORM_STATE_STARTED': {
      return {
        ...state,
        syncingFormState: true,
        formStateLoadError: null,
      };
    }
    case 'GET_FORM_STATE_RESET': {
      return {
        ...state,
        syncingFormState: false,
        formStateLoadError: null,
      };
    }
    case 'GET_FORM_STATE_REJECTED': {
      return {
        ...state,
        syncingFormState: false,
        error: normaliseError(action.error),
        formStateLoadError: {
          applicationId: String(action.applicationId),
          error: normaliseError(action.error),
        },
      };
    }
    case 'GET_FORM_STATE_PARTIAL_FULFILLED': {
      const { id: applicationId, emissions } = action;
      if (!applicationId) {
        return state;
      }
      if (!state.items) {
        return state;
      }
      const applications = state.items.map((item) => {
        if (item.id !== applicationId) {
          // This isn't the item we care about - keep it as-is
          return item;
        }
        return {
          ...item,
          emissions,
        };
      });

      return {
        ...state,
        items: applications,
        syncingFormState: false,
        formStateLoadError: null,
      };
    }
    case 'GET_FORM_STATE_FULFILLED': {
      // The application form state includes emissions data, this data needs to be appended onto the relevant application
      const {
        id: applicationId,
        emissions,
        averages,
        paymentAwaitingProcessing,
        offset,
        applicationState,
        basketItems,
        basketMeta,
        questionAnswerStructured,
      } = action;
      if (!applicationId) {
        return state;
      }

      if (!state.items) {
        return state;
      }

      const applications = state.items.map((item) => {
        if (item.id !== applicationId) {
          // This isn't the item we care about - keep it as-is
          return item;
        }

        // Otherwise, this is the one we want - return the application with emissions data appended onto it
        return {
          ...item,
          emissions,
          averages,
          paymentAwaitingProcessing,
          offset,
          applicationState,
          basketItems,
          basketMeta,
          questionAnswerStructured,
        };
      });

      return {
        ...state,
        items: applications,
        syncingFormState: false,
        formStateLoadError: null,
      };
    }

    case 'MARK_APPLICATION_PAYMENT_PENDING_STATE': {
      if (!action.applicationId) {
        return state;
      }
      if (!state.items) {
        return state;
      }
      const { applicationId, isDone } = action;
      const applications = state.items.map((item) => {
        if (item.id !== applicationId) {
          return item;
        }
        const awaitingPaymentVal = !isDone;
        return {
          ...item,
          paymentAwaitingProcessing: awaitingPaymentVal,
        };
      });
      return {
        ...state,
        items: applications,
      };
    }
    case 'EDIT_APPLICATION_REJECTED': {
      return {
        ...state,
        saving: false,
        saved: false,
        error: normaliseError(action.error),
      };
    }
    case 'TRANSITION_APPLICATION_STARTED': {
      return {
        ...state,
        saving: true,
        saved: false,
        error: null,
      };
    }
    case 'APPLICATION_LOAD_START': {
      return {
        ...state,
        activeApplicationId: String(action.applicationId),
        fetching: true,
        error: null,
        applicationLoadError: null,
      };
    }
    case 'APPLICATION_LOAD_FULFILLED': {
      if (!action.application || !action.application.id) {
        return state;
      }
      // If an application has been edited, we need to insert the value into the applications listing
      const { application } = action;

      if (!state.items || state.items.length === 0) {
        return {
          ...state,
          fetching: false,
          applicationLoadError: null,
          items: [application],
        };
      }

      if (!state.items.find((item) => item.id === application.id)) {
        return {
          ...state,
          fetching: false,
          applicationLoadError: null,
          items: [...state.items, application],
        };
      }

      // If the application is in the state, update it
      const applications = state.items.map((item) => {
        if (item.id !== application.id) {
          return item;
        }

        // Otherwise, this is the one we want - return an updated value
        return {
          ...item,
          ...application,
        };
      });

      return {
        ...state,
        fetching: false,
        saving: false,
        saved: true,
        error: null,
        applicationLoadError: null,
        items: applications,
      };
    }
    case 'APPLICATION_LOAD_REJECTED': {
      return {
        ...state,
        fetching: false,
        error: action.error ? normaliseError(action.error) : state.error,
        applicationLoadError: action.error
          ? {
              applicationId: String(action.applicationId),
              error: normaliseError(action.error),
            }
          : state.applicationLoadError,
      };
    }
    case 'TRANSITION_APPLICATION_REJECTED': {
      return {
        ...state,
        saving: false,
        saved: true,
        error: normaliseError(action.error),
      };
    }
    case 'SAVE_INVITE_STATUS_FULFILLED': {
      const { application } = action;

      if (application) {
        let isExistingApplication = false;

        const currentItems = state.items || [];

        const applications = currentItems.map((item) => {
          if (item.id !== application.id) {
            return item;
          }

          isExistingApplication = true;
          return {
            ...item,
            ...application,
          };
        });

        if (!isExistingApplication) {
          applications.push(application);
        }
        return {
          ...state,
          items: applications,
          hasInitiallySyncedApplications: false,
        };
      }
      return {
        ...state,
        hasInitiallySyncedApplications: false,
      };
    }
    case 'SEND_INVITE_STATUS_FULFILLED': {
      const { invite = null, id = null } = action;
      if (invite && state.items) {
        const applications = state.items.map((item) => {
          if (item.id !== id) {
            return item;
          }

          const previousAssociatedUsers = item?.invites || [];
          const nextAssociatedUsers = previousAssociatedUsers.concat(invite);

          return {
            ...item,
            invites: nextAssociatedUsers,
          };
        });

        return {
          ...state,
          items: applications,
        };
      }
      return {
        ...state,
      };
    }
    case 'REMOVE_INVITE_STATUS_FULFILLED': {
      const { removedId = null, applicationId = null } = action;
      if (removedId && applicationId && state.items) {
        const applications = state.items.map((item) => {
          if (item.id !== applicationId) {
            return item;
          }

          const previousAssociatedUsers = item?.invites || [];
          const nextAssociatedUsers = previousAssociatedUsers.filter((previousAssociatedUser) => {
            return previousAssociatedUser.id !== removedId;
          });

          return {
            ...item,
            invites: nextAssociatedUsers,
          };
        });

        return {
          ...state,
          items: applications,
        };
      }
      return {
        ...state,
      };
    }

    case 'GET_APPLICATION_PACKAGES_STARTED': {
      return {
        ...state,
        fetchingPackages: true,
      };
    }

    case 'GET_APPLICATION_PACKAGES_FULFILLED': {
      return {
        ...state,
        fetchingPackages: false,
        packages: action.applicationPackages,
        fetchedPackages: true,
      };
    }

    case 'GET_APPLICATION_PACKAGES_REJECTED': {
      return {
        ...state,
        fetchingPackages: false,
      };
    }

    case 'GET_ANNUAL_UPDATES_STARTED': {
      return {
        ...state,
        fetchingAnnualUpdates: true,
      };
    }
    case 'GET_ANNUAL_UPDATES_FULFILLED': {
      return {
        ...state,
        fetchingAnnualUpdates: false,
        fetchedAnnualUpdates: true,
        annualUpdates: action.annualUpdates,
      };
    }
    case 'GET_ANNUAL_UPDATES_REJECTED': {
      return {
        ...state,
        fetchingAnnualUpdates: false,
        fetchedAnnualUpdates: true,
        annualUpdates: null,
        error: normaliseError(action.error),
      };
    }

    case 'ENABLE_ANNUAL_UPDATE_STARTED': {
      return {
        ...state,
        saving: true,
        saved: false,
        error: null,
      };
    }
    case 'ENABLE_ANNUAL_UPDATE_FULFILLED': {
      // Mark the source package as having a follow-on
      const updatedAnnualUpdates =
        state.annualUpdates?.map((item) => {
          if (item.packageId === action.packageId) {
            return { ...item, hasFollowOnApplication: true };
          }
          return item;
        }) ?? null;

      return {
        ...state,
        saving: false,
        saved: true,
        error: null,
        annualUpdates: updatedAnnualUpdates,
      };
    }
    case 'ENABLE_ANNUAL_UPDATE_REJECTED': {
      return {
        ...state,
        saving: false,
        saved: false,
        error: normaliseError(action.error),
      };
    }

    case 'ARCHIVE_PACKAGE_STARTED': {
      return {
        ...state,
        saving: true,
        saved: false,
        error: null,
      };
    }
    case 'ARCHIVE_PACKAGE_FULFILLED': {
      // Remove the archived package's applications from annual updates
      const filteredAnnualUpdates = state.annualUpdates?.filter((item) => item.packageId !== action.packageId) ?? null;

      return {
        ...state,
        saving: false,
        saved: true,
        error: null,
        annualUpdates: filteredAnnualUpdates,
      };
    }
    case 'ARCHIVE_PACKAGE_REJECTED': {
      return {
        ...state,
        saving: false,
        saved: false,
        error: normaliseError(action.error),
      };
    }

    case 'MARK_ANNUAL_UPDATE_COMPLETE_STARTED': {
      return {
        ...state,
        saving: true,
        saved: false,
        error: null,
      };
    }
    case 'MARK_ANNUAL_UPDATE_COMPLETE_FULFILLED': {
      return {
        ...state,
        saving: false,
        saved: true,
        error: null,
      };
    }
    case 'MARK_ANNUAL_UPDATE_COMPLETE_REJECTED': {
      return {
        ...state,
        saving: false,
        saved: false,
        error: normaliseError(action.error),
      };
    }

    case 'GET_APPLICATION_PACKAGE_COMPARISON_STARTED': {
      return {
        ...state,
        fetchingComparison: true,
        comparison: null,
      };
    }
    case 'GET_APPLICATION_PACKAGE_COMPARISON_FULFILLED': {
      return {
        ...state,
        fetchingComparison: false,
        comparison: action.payload,
      };
    }
    case 'GET_APPLICATION_PACKAGE_COMPARISON_REJECTED': {
      return {
        ...state,
        fetchingComparison: false,
        comparison: null,
        error: normaliseError(action.error),
      };
    }

    case 'SET_APPLICATION_PACKAGE_BASELINE_YEAR_STARTED': {
      return {
        ...state,
        saving: true,
        saved: false,
        error: null,
      };
    }
    case 'SET_APPLICATION_PACKAGE_BASELINE_YEAR_FULFILLED': {
      return {
        ...state,
        saving: false,
        saved: true,
        error: null,
        comparison: action.payload,
      };
    }
    case 'SET_APPLICATION_PACKAGE_BASELINE_YEAR_REJECTED': {
      return {
        ...state,
        saving: false,
        saved: false,
        error: normaliseError(action.error),
      };
    }

    case 'GET_COPIED_FROM_SNAPSHOTS_STARTED': {
      return {
        ...state,
        fetchingCopiedFromSnapshots: true,
        copiedFromSnapshots: null,
      };
    }
    case 'GET_COPIED_FROM_SNAPSHOTS_FULFILLED': {
      return {
        ...state,
        fetchingCopiedFromSnapshots: false,
        copiedFromSnapshots: action.payload,
      };
    }
    case 'GET_COPIED_FROM_SNAPSHOTS_REJECTED': {
      return {
        ...state,
        fetchingCopiedFromSnapshots: false,
        copiedFromSnapshots: null,
        error: normaliseError(action.error),
      };
    }
    default:
      return state;
  }
}

export default reducer;
